import { useState, useMemo, useCallback } from 'react';
import {
  PromanReport,
  PromanReportCollection,
} from '../../../../../api/promanDtos';
import { groupCollectionsByFolder } from '../utils/promanHelpers';

interface UsePromanNavigationReturn {
  // State
  activeFolders: (string | number)[];
  activeCollections: (string | number)[];
  activeReports: (string | number)[];
  selectedReport: PromanReport | null;
  searchQuery: string;

  // Computed
  folderGroups: Record<number, PromanReportCollection[]>;
  folderIds: string[];

  // Handlers
  handleFolderChange: (keys: (string | number) | (string | number)[]) => void;
  handleCollectionChange: (
    keys: (string | number) | (string | number)[]
  ) => void;
  handleReportChange: (keys: (string | number) | (string | number)[]) => void;
  handleReportClick: (report: PromanReport) => void;
  setSearchQuery: (query: string) => void;
}

export const usePromanNavigation = (
  reportCollections: PromanReportCollection[],
  onCollectionExpand?: (collectionId: number) => void
): UsePromanNavigationReturn => {
  const [activeFolders, setActiveFolders] = useState<(string | number)[]>([]);
  const [activeCollections, setActiveCollections] = useState<
    (string | number)[]
  >([]);
  const [activeReports, setActiveReports] = useState<(string | number)[]>([]);
  const [selectedReport, setSelectedReport] = useState<PromanReport | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Group collections by folderID
  const folderGroups = useMemo(() => {
    return groupCollectionsByFolder(reportCollections);
  }, [reportCollections]);

  // Get all folder IDs for initial expanded state
  const folderIds = useMemo(() => {
    return Object.keys(folderGroups).map((id) => `folder-${id}`);
  }, [folderGroups]);

  // Handle folder expand/collapse
  const handleFolderChange = useCallback(
    (keys: (string | number) | (string | number)[]) => {
      const keyArray = Array.isArray(keys) ? keys : [keys];
      setActiveFolders(keyArray);
    },
    []
  );

  // Handle collection expand/collapse
  const handleCollectionChange = useCallback(
    (keys: (string | number) | (string | number)[]) => {
      const keyArray = Array.isArray(keys) ? keys : [keys];
      setActiveCollections(keyArray);

      // Trigger loading for newly expanded collections
      if (onCollectionExpand) {
        keyArray.forEach((key) => {
          const collectionId = typeof key === 'string' ? parseInt(key) : key;
          if (!isNaN(collectionId)) {
            onCollectionExpand(collectionId);
          }
        });
      }
    },
    [onCollectionExpand]
  );

  // Handle report expand/collapse
  const handleReportChange = useCallback(
    (keys: (string | number) | (string | number)[]) => {
      const keyArray = Array.isArray(keys) ? keys : [keys];
      setActiveReports(keyArray);
    },
    []
  );

  // Handle report click/selection
  const handleReportClick = useCallback((report: PromanReport) => {
    setSelectedReport(report);
  }, []);

  return {
    activeFolders,
    activeCollections,
    activeReports,
    selectedReport,
    searchQuery,
    folderGroups,
    folderIds,
    handleFolderChange,
    handleCollectionChange,
    handleReportChange,
    handleReportClick,
    setSearchQuery,
  };
};
